/** People and leads, with a shortcut straight into WhatsApp. */
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { useApp } from '../store/AppContext.jsx';
import Icon from '../lib/icons.jsx';
import { digitsOf } from '../lib/format.js';
import {
  Card, CardHead, EmptyState, Avatar, LeadPill, Pill, Dash
} from '../components/Primitives.jsx';
import { TileRow } from '../components/StatCards.jsx';
import CrmFilter from '../components/CrmFilter.jsx';
import { filterContacts, companyName, contactOf } from '../domain/crm.js';

export default function Contacts() {
  const { crm, cF, setCF, pipeline, setSheet, removeFromCrm, addContactToPipeline, toast } = useApp();
  const navigate = useNavigate();
  const pick = key => setCF(f => ({ ...f, tile: f.tile === key ? '' : key }));
  const tileOn = key => cF.tile === key;
  const list = useMemo(() => filterContacts(crm, cF), [crm, cF]);

  const remove = (e, id) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this contact?')) removeFromCrm('contacts', id);
  };

  const whatsapp = (e, id) => {
    e.stopPropagation();
    const c = contactOf(crm, id);
    if (!digitsOf(c?.phone)) { toast('No phone number'); return; }
    window.open(`https://wa.me/${digitsOf(c.phone)}`, '_blank');
  };

  return (
    <>
      <TileRow
        tiles={[
          ['Contacts', crm.contacts.length, 'users', 'g-blue', () => pick(''), !cF.tile],
          ['Leads', pipeline.leads, 'zap', 'g-amber', () => pick('leads'), tileOn('leads')],
          ['Customers', crm.contacts.filter(c => c.status === 'Customer').length, 'check', 'g-green', () => pick('customers'), tileOn('customers')],
          ['Unassigned', crm.contacts.filter(c => !c.owner).length, 'alert', 'g-red', () => pick('unassigned'), tileOn('unassigned')]
        ]}
      />

      <CrmFilter kind="contact" />

      <Card>
        <CardHead icon="users" tone="g-blue" title="Contacts" count={list.length}>
          {/*
            * A list of companies lands here, not on the board.
            *
            * A contact is the lighter record — somebody we know of, not yet
            * a lead — which is the right place for a bought or exported
            * list: most of it is people nobody has spoken to. The ones that
            * turn out to matter go onto the pipeline from the row, and the
            * board says they came in as a Contact.
            *
            * It runs the same check against Clients the pipeline import
            * runs: somebody who has registered is a client, not a lead, and
            * not a contact to be sold to either.
            */}
          <button
            type="button"
            className="btn"
            onClick={() => setSheet({ kind: 'importContacts' })}
            title="Bring a spreadsheet of companies into Contacts"
          >
            <Icon name="up" />Import
          </button>
        </CardHead>

        {list.length ? (
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Company</th><th>Status</th><th>Source</th>
                  <th>Owner</th><th>Email</th><th>Phone</th><th>Deals</th><th />
                </tr>
              </thead>
              <tbody>
                {list.map(c => (
                  <tr key={c.id} onClick={() => setSheet({ kind: 'contact', id: c.id })}>
                    <td>
                      <div className="cname">
                        <Avatar name={c.name} />
                        <div><b>{c.name}</b><small>{c.title || ''}</small></div>
                      </div>
                    </td>
                    <td>{companyName(crm, c.cid)}</td>
                    <td><LeadPill value={c.status} /></td>
                    <td className="dim">{c.source || '—'}</td>
                    <td>{c.owner || <Pill tone="p-red">Unowned</Pill>}</td>
                    <td className="email">
                      {c.email
                        ? <a href={`mailto:${c.email}`} style={{ color: 'var(--brand)', textDecoration: 'none' }}>{c.email}</a>
                        : <Dash />}
                    </td>
                    <td className="dim">{c.phone || '—'}</td>
                    <td>{crm.deals.filter(d => d.contact === c.id).length}</td>
                    <td>
                      <div className="acts">
                        {/* The way a contact becomes a customer: onto the
                            board, up the stages, won, linked to the account. */}
                        <button
                          type="button"
                          className="won"
                          title="Add to pipeline"
                          onClick={e => {
                            e.stopPropagation();
                            if (addContactToPipeline(c.id)) navigate('/pipeline', { state: { keepFilter: true } });
                          }}
                        >
                          <Icon name="zap" />
                        </button>
                        <button
                          type="button"
                          title="Edit"
                          onClick={e => { e.stopPropagation(); setSheet({ kind: 'contact', id: c.id }); }}
                        >
                          <Icon name="edit" />
                        </button>
                        <button type="button" title="WhatsApp" onClick={e => whatsapp(e, c.id)}>
                          <Icon name="phone" />
                        </button>
                        <button type="button" className="rm" title="Delete" onClick={e => remove(e, c.id)}>
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="users" tone="g-blue" title="No contacts match">
            Clear the filters, or add one.
          </EmptyState>
        )}
      </Card>
    </>
  );
}
